import "./ModalImportFromCanva.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { NavLink } from "react-router-dom";
import {
	HEYGEN_APP_URL,
	HEYGEN_MAX_BATCH_SIZE,
	type HeyGenImportItemDto,
	type HeyGenImportStatusDto,
	type HeyGenVideoDto,
} from "shared";
import { useDebounceValue } from "usehooks-ts";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { Dialog, useDialog } from "../common/dialog/Dialog";
import { parseError } from "../common/error";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { Spinner } from "../common/Spinner";
import { useCompany } from "../company/useCompany";
import { Button } from "../components/Button";
import { LinkGen, QueryKeys } from "../LinkGen";
import { useHeyGenQuery } from "../settings/useIntegrations";
import { useHeyGenImportStream } from "./useHeyGenImportStream";

const PAGE_SIZE = 20;

const formatDuration = (seconds: number | null) => {
	if (seconds === null) {
		return null;
	}
	const total = Math.round(seconds);
	return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

const isReconnectError = (error: unknown) =>
	(parseError(error) ?? "").toLowerCase().includes("reconnect");

function VideoCard(props: {
	video: HeyGenVideoDto;
	selected: boolean;
	onToggle: (video: HeyGenVideoDto) => void;
	onThumbnailError: (video: HeyGenVideoDto) => void;
}) {
	const { video, selected, onToggle, onThumbnailError } = props;
	const rendering = video.status !== "completed";
	const disabled = rendering || video.alreadyImported;
	const duration = formatDuration(video.durationSeconds);

	return (
		<button
			type="button"
			className={`canva-design-card${selected ? " selected" : ""}`}
			onClick={() => !disabled && onToggle(video)}
			aria-pressed={selected}
			aria-disabled={disabled}
			style={disabled ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
			title={rendering ? "Still rendering in HeyGen" : undefined}
		>
			<div className="canva-design-thumb">
				{video.thumbnailUrl ? (
					<img
						src={video.thumbnailUrl}
						alt=""
						loading="lazy"
						onError={() => onThumbnailError(video)}
					/>
				) : (
					<div className="canva-design-thumb-placeholder">No preview</div>
				)}
				{video.alreadyImported && (
					<span className="canva-design-imported-badge">Imported</span>
				)}
				{rendering && (
					<span className="canva-design-imported-badge">Still rendering</span>
				)}
				{selected && <span className="canva-design-check">✓</span>}
			</div>
			<div className="canva-design-meta">
				<div className="canva-design-title" title={video.title}>
					{video.title}
				</div>
				<div className="canva-design-date">
					{[
						duration,
						video.createdAt
							? new Date(video.createdAt).toLocaleDateString()
							: null,
						video.hasCaptions ? "Captions" : null,
						video.hasSubtitles ? "Subtitles" : null,
					]
						.filter(Boolean)
						.join(" · ")}
				</div>
			</div>
		</button>
	);
}

const ITEM_STATUS_LABEL: Record<HeyGenImportItemDto["status"], string> = {
	pending: "Queued",
	downloading: "Downloading…",
	processing: "Processing…",
	done: "Done",
	failed: "Failed",
};

function ImportProgressPanel(props: {
	status: HeyGenImportStatusDto;
	onRetry: (item: HeyGenImportItemDto) => void;
	retryingId: number | null;
}) {
	const { status, onRetry, retryingId } = props;
	const finished = status.succeeded + status.failed;
	const percent =
		status.totalAssets > 0
			? Math.round((finished / status.totalAssets) * 100)
			: 0;

	return (
		<div className="canva-import-progress">
			<div className="canva-import-progress-header">
				<strong>
					Importing {finished} / {status.totalAssets}
				</strong>
				<span className="text-muted">
					{status.status === "in_progress" ? "In progress…" : "Complete"}
				</span>
			</div>
			<div
				className="canva-import-progress-bar"
				role="progressbar"
				aria-valuenow={percent}
				aria-valuemin={0}
				aria-valuemax={100}
			>
				<div
					className="canva-import-progress-bar-fill"
					style={{ width: `${percent}%` }}
				/>
			</div>
			<ul className="canva-import-progress-list">
				{status.items.map((item) => (
					<li
						key={item.id}
						className={`canva-import-item status-${item.status === "done" || item.status === "failed" ? item.status : "in_progress"}`}
					>
						<span className="canva-import-item-title" title={item.title}>
							{item.title}
						</span>
						<span className="canva-import-item-status">
							{item.status === "failed" || item.status === "pending"
								? (item.errorMessage ?? ITEM_STATUS_LABEL[item.status])
								: ITEM_STATUS_LABEL[item.status]}
						</span>
						{item.status === "failed" && (
							<Button
								size="sm"
								variant="secondary"
								loading={retryingId === item.id}
								onClick={() => onRetry(item)}
							>
								Retry
							</Button>
						)}
					</li>
				))}
			</ul>
		</div>
	);
}

export function ModalImportFromHeyGen(props: { folderId?: number | null }) {
	const dialog = useDialog();
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();
	const { data: heygenStatus } = useHeyGenQuery();
	const connectionInvalid =
		heygenStatus?.connected === true && heygenStatus.state === "invalid";

	const [searchWord, setSearchWord] = useState("");
	const [debouncedSearch] = useDebounceValue(searchWord.trim(), 400);
	const [token, setToken] = useState<string | null>(null);
	const [videos, setVideos] = useState<HeyGenVideoDto[]>([]);
	const [selected, setSelected] = useState<Map<string, HeyGenVideoDto>>(
		() => new Map(),
	);
	const [captioned, setCaptioned] = useState(false);
	const [importId, setImportId] = useState<number | null>(null);
	const [streamGeneration, setStreamGeneration] = useState(0);
	const [retryingId, setRetryingId] = useState<number | null>(null);
	const [folderFilter, setFolderFilter] = useState("");
	const [seenFolderIds, setSeenFolderIds] = useState<string[]>([]);
	const thumbnailRefreshTried = useRef(new Set<string>());

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset results when the filters change
	useEffect(() => {
		setToken(null);
		setVideos([]);
	}, [debouncedSearch, folderFilter]);

	const videosQuery = useQuery({
		queryKey: QueryKeys.Integrations.HeyGen.Videos(
			companyId,
			token,
			debouncedSearch,
			folderFilter,
		),
		queryFn: async () =>
			(
				await Api.HeyGenListVideos({
					token: token ?? undefined,
					limit: PAGE_SIZE,
					title: debouncedSearch || undefined,
					folderId: folderFilter || undefined,
				})
			).data,
		enabled:
			(heygenStatus?.connected ?? false) &&
			!connectionInvalid &&
			importId === null,
		retry: false,
	});

	const needsReconnect =
		connectionInvalid ||
		(videosQuery.isError && isReconnectError(videosQuery.error));

	useEffect(() => {
		if (videosQuery.isError && isReconnectError(videosQuery.error)) {
			void queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.HeyGen.Status(companyId),
			});
		}
	}, [videosQuery.isError, videosQuery.error, companyId, queryClient]);

	useEffect(() => {
		if (!videosQuery.data || videosQuery.isFetching) {
			return;
		}
		setVideos((prev) => {
			if (!token) {
				return videosQuery.data.items;
			}
			const seen = new Set(prev.map((v) => v.id));
			return [
				...prev,
				...videosQuery.data.items.filter((v) => !seen.has(v.id)),
			];
		});
		const pageFolderIds = videosQuery.data.items
			.map((v) => v.folderId)
			.filter((id): id is string => !!id);
		setSeenFolderIds((prev) => {
			const merged = Array.from(new Set([...prev, ...pageFolderIds]));
			return merged.length === prev.length ? prev : merged;
		});
	}, [videosQuery.data, videosQuery.isFetching, token]);

	const foldersQuery = useQuery({
		queryKey: QueryKeys.Integrations.HeyGen.Folders(companyId, seenFolderIds),
		queryFn: async () => (await Api.HeyGenListFolders(seenFolderIds)).data,
		enabled: seenFolderIds.length > 0 && !connectionInvalid,
		staleTime: 10 * 60 * 1000,
		retry: false,
	});
	const folderNames = new Map(
		(foldersQuery.data ?? []).map((f) => [f.id, f.name]),
	);

	const refreshThumbnail = useCallback(async (video: HeyGenVideoDto) => {
		if (thumbnailRefreshTried.current.has(video.id)) {
			return;
		}
		thumbnailRefreshTried.current.add(video.id);
		try {
			const fresh = (await Api.HeyGenVideoDetails(video.id)).data;
			setVideos((prev) =>
				prev.map((v) =>
					v.id === video.id ? { ...v, thumbnailUrl: fresh.thumbnailUrl } : v,
				),
			);
		} catch {
			setVideos((prev) =>
				prev.map((v) => (v.id === video.id ? { ...v, thumbnailUrl: null } : v)),
			);
		}
	}, []);

	useHeyGenImportStream(companyId, importId, streamGeneration);

	const importStatusQuery = useQuery({
		queryKey: QueryKeys.Integrations.HeyGen.Import(companyId, importId ?? 0),
		queryFn: async () =>
			(await Api.HeyGenImportStatus(importId as number)).data,
		enabled: importId !== null,
		staleTime: Number.POSITIVE_INFINITY,
	});

	const importFinished =
		importStatusQuery.data !== undefined &&
		importStatusQuery.data.status !== "in_progress";

	useEffect(() => {
		if (importFinished) {
			void queryClient.invalidateQueries({
				queryKey: QueryKeys.Files.All(companyId),
			});
			void queryClient.removeQueries({
				queryKey: QueryKeys.Integrations.HeyGen.Videos(
					companyId,
					null,
					"",
					"",
				).slice(0, 5),
			});
		}
	}, [importFinished, companyId, queryClient]);

	const startImport = useMutation({
		mutationFn: async () =>
			(
				await Api.HeyGenStartImport({
					videos: Array.from(selected.values()).map((v) => ({
						videoId: v.id,
						title: v.title,
					})),
					captioned,
					folderId: props.folderId ?? null,
				})
			).data,
		onSuccess: (data) => {
			setImportId(data.id);
			queryClient.setQueryData(
				QueryKeys.Integrations.HeyGen.Import(companyId, data.id),
				data,
			);
		},
		onError: (error) => {
			emit({
				type: AlertType.ERROR,
				content: parseError(error) ?? "Failed to start the HeyGen import.",
			});
		},
	});

	const toggleVideo = useCallback((video: HeyGenVideoDto) => {
		setSelected((prev) => {
			const next = new Map(prev);
			if (next.has(video.id)) {
				next.delete(video.id);
			} else if (next.size < HEYGEN_MAX_BATCH_SIZE) {
				next.set(video.id, video);
			}
			return next;
		});
	}, []);

	const handleRetry = useCallback(
		async (item: HeyGenImportItemDto) => {
			setRetryingId(item.id);
			try {
				await Api.HeyGenRetryImportItem(item.id);
				setStreamGeneration((g) => g + 1);
			} catch (error) {
				emit({
					type: AlertType.ERROR,
					content: parseError(error) ?? "Retry failed. Please try again.",
				});
			} finally {
				setRetryingId(null);
			}
		},
		[emit],
	);

	const selectedCount = selected.size;
	const isLoading =
		videos.length === 0 && (videosQuery.isLoading || videosQuery.isFetching);
	const showBrowse = importId === null || !importStatusQuery.data;
	const anyWithCaptions = Array.from(selected.values()).some(
		(v) => v.hasCaptions,
	);

	return (
		<Dialog className="canva-import-modal">
			<Dialog.Header title="Import from HeyGen" />
			<Dialog.Body>
				<p className="canva-import-subtitle">
					Pick finished HeyGen videos to copy into your media library. Up to{" "}
					{HEYGEN_MAX_BATCH_SIZE} per batch.
					{selectedCount > 0 && (
						<span className="canva-import-selected-count">
							{" "}
							· {selectedCount} selected
						</span>
					)}
				</p>

				{showBrowse ? (
					needsReconnect ? (
						<div className="canva-import-reconnect">
							<p className="text-center text-muted mb-0">
								HeyGen stopped accepting your API key. Reconnect HeyGen to
								browse and import videos.
							</p>
							<NavLink to={LinkGen.Settings.Apps()}>
								<Button>Reconnect HeyGen</Button>
							</NavLink>
						</div>
					) : (
						<>
							<div className="canva-import-toolbar">
								<ExtraForm.Control
									value={searchWord}
									onChange={(e) => setSearchWord(e.target.value)}
									placeholder="Search videos by title"
									type="search"
									aria-label="Search HeyGen videos"
								/>
								<ExtraForm.Select
									value={folderFilter}
									onChange={(e) => setFolderFilter(e.target.value)}
									aria-label="Filter by HeyGen folder"
									disabled={seenFolderIds.length === 0 && !folderFilter}
								>
									<option value="">All folders</option>
									{seenFolderIds.map((id) => (
										<option key={id} value={id}>
											{folderNames.get(id) ?? `Folder ${id.slice(0, 8)}`}
										</option>
									))}
								</ExtraForm.Select>
							</div>

							<div className="canva-import-content">
								{isLoading ? (
									<div className="canva-import-loading">
										<Spinner />
									</div>
								) : (
									<div className="canva-design-grid">
										{videos.map((video) => (
											<VideoCard
												key={video.id}
												video={video}
												selected={selected.has(video.id)}
												onToggle={toggleVideo}
												onThumbnailError={refreshThumbnail}
											/>
										))}
									</div>
								)}

								{videos.length === 0 &&
									!isLoading &&
									!videosQuery.isError &&
									(debouncedSearch || folderFilter ? (
										<p className="canva-import-empty">
											{debouncedSearch
												? `No videos matching "${debouncedSearch}".`
												: "No videos in this folder."}
										</p>
									) : (
										<div className="canva-import-reconnect">
											<p className="text-center text-muted mb-0">
												No finished videos in this HeyGen account yet. Create
												one in HeyGen, then come back to import it.
											</p>
											<a
												href={HEYGEN_APP_URL}
												target="_blank"
												rel="noopener noreferrer"
											>
												<Button variant="secondary">Open HeyGen</Button>
											</a>
										</div>
									))}

								{videosQuery.isError && !needsReconnect && (
									<p className="canva-import-empty">
										{parseError(videosQuery.error) ??
											"Couldn't load your HeyGen videos. Try again."}
									</p>
								)}

								{videosQuery.data?.hasMore && videosQuery.data.nextToken && (
									<div className="canva-import-load-more">
										<Button
											variant="secondary"
											size="sm"
											loading={videosQuery.isFetching}
											onClick={() =>
												setToken(videosQuery.data?.nextToken ?? null)
											}
										>
											Load more
										</Button>
									</div>
								)}
							</div>

							{anyWithCaptions && (
								<Form.Check
									type="switch"
									id="heygen-import-captioned"
									label="Import the captioned version when available"
									checked={captioned}
									onChange={() => setCaptioned((v) => !v)}
									className="mt-3"
								/>
							)}
						</>
					)
				) : (
					<ImportProgressPanel
						status={importStatusQuery.data}
						onRetry={handleRetry}
						retryingId={retryingId}
					/>
				)}
			</Dialog.Body>

			<Dialog.Footer className="actions">
				{showBrowse ? (
					<>
						<Button variant="secondary" onClick={() => dialog.close()}>
							{needsReconnect ? "Close" : "Cancel"}
						</Button>
						{!needsReconnect && (
							<Button
								onClick={() => startImport.mutate()}
								loading={startImport.isPending}
								disabled={selectedCount === 0}
							>
								Import{selectedCount > 0 ? ` ${selectedCount}` : ""}
							</Button>
						)}
					</>
				) : importFinished ? (
					<Button onClick={() => dialog.close()}>Done</Button>
				) : (
					<Button variant="secondary" onClick={() => dialog.close()}>
						Import in background
					</Button>
				)}
			</Dialog.Footer>
		</Dialog>
	);
}
